import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FiUpload,
  FiSearch,
  FiEye,
  FiShare2,
  FiDownload,
  FiTrash2,
  FiEdit2,
  FiX,
  FiFilter,
  FiGrid,
  FiList,
  FiRefreshCw,
  FiFile,
  FiFileText,
  FiImage,
  FiFilm,
  FiMusic,
  FiArchive,
  FiMoreVertical,
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import FileShareModal from '../components/FileShareModal';
import FileViewer, { isOfficeFile, needsFillPreview } from '../components/FileViewer';
import './FileExchange.css';

const MAX_FILE_SIZE = 250 * 1024 * 1024;
const VIEW_MODE_KEY = 'fileExchangeViewMode';

const CATEGORY_OPTIONS = [
  { value: 'all', label: 'Все типы' },
  { value: 'image', label: 'Изображения' },
  { value: 'video', label: 'Видео' },
  { value: 'audio', label: 'Аудио' },
  { value: 'document', label: 'Документы' },
  { value: 'archive', label: 'Архивы' },
  { value: 'other', label: 'Прочее' },
];

const SORT_OPTIONS = [
  { value: '-created_at', label: 'Сначала новые' },
  { value: 'created_at', label: 'Сначала старые' },
  { value: 'original_filename', label: 'Имя (А-Я)' },
  { value: '-original_filename', label: 'Имя (Я-А)' },
  { value: '-file_size', label: 'Размер (больше)' },
  { value: 'file_size', label: 'Размер (меньше)' },
  { value: 'expires_at', label: 'Срок удаления' },
];

const getCategoryIcon = (category) => {
  switch (category) {
    case 'image': return FiImage;
    case 'video': return FiFilm;
    case 'audio': return FiMusic;
    case 'document': return FiFileText;
    case 'archive': return FiArchive;
    default: return FiFile;
  }
};

const formatDate = (value) => {
  if (!value) return '—';
  return new Date(value).toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const FileActions = ({ file, canShare, onPreview, onDownload, onShare, onEdit, onDelete, compact, showShare = true }) => (
  <div className={`nc-file-actions ${compact ? 'compact' : ''}`}>
    <button type="button" className="nc-icon-btn" title="Просмотр" onClick={() => onPreview(file)}>
      <FiEye />
    </button>
    <button type="button" className="nc-icon-btn" title="Скачать" onClick={() => onDownload(file, true)}>
      <FiDownload />
    </button>
    {canShare && showShare && (
      <button type="button" className="nc-icon-btn" title="Поделиться" onClick={() => onShare(file)}>
        <FiShare2 />
      </button>
    )}
    <button type="button" className="nc-icon-btn" title="Редактировать" onClick={() => onEdit(file)}>
      <FiEdit2 />
    </button>
    <button type="button" className="nc-icon-btn danger" title="Удалить" onClick={() => onDelete(file)}>
      <FiTrash2 />
    </button>
  </div>
);

const FileExchange = () => {
  const { isModerator, canShare } = useAuth();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [ordering, setOrdering] = useState('-created_at');
  const [viewMode, setViewMode] = useState(() => localStorage.getItem(VIEW_MODE_KEY) || 'grid');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ category: 'all', ordering: '-created_at' });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [expiresAtTime, setExpiresAtTime] = useState('');
  const [shareFile, setShareFile] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [editingFile, setEditingFile] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const [editExpiresAt, setEditExpiresAt] = useState('');
  const [editExpiresAtTime, setEditExpiresAtTime] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ordering };
      if (search) params.search = search;
      if (category !== 'all') params.file_category = category;
      const response = await axios.get('/api/files/', { params });
      const data = Array.isArray(response.data) ? response.data : (response.data?.results || []);
      setFiles(data);
    } catch (error) {
      toast.error('Ошибка загрузки файлов');
    } finally {
      setLoading(false);
    }
  }, [search, category, ordering]);

  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  useEffect(() => () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
  }, [previewUrl]);

  useEffect(() => {
    localStorage.setItem(VIEW_MODE_KEY, viewMode);
  }, [viewMode]);

  useEffect(() => {
    if (!openMenuId) return undefined;
    const closeMenu = () => setOpenMenuId(null);
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, [openMenuId]);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (category !== 'all') count += 1;
    if (ordering !== '-created_at') count += 1;
    return count;
  }, [category, ordering]);

  const buildExpiresAtIso = (dateValue, timeValue) => {
    if (!dateValue) return null;
    const time = timeValue || '23:59';
    return new Date(`${dateValue}T${time}`).toISOString();
  };

  const resetUploadForm = () => {
    setSelectedFile(null);
    setDescription('');
    setExpiresAt('');
    setExpiresAtTime('');
    setUploadProgress(0);
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    resetUploadForm();
  };

  const handleFilePick = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Максимальный размер файла — 250 МБ');
      return;
    }
    setSelectedFile(file);
  };

  const handleUploadSubmit = async () => {
    if (!selectedFile) {
      toast.error('Выберите файл');
      return;
    }
    const formData = new FormData();
    formData.append('file', selectedFile);
    if (description.trim()) formData.append('description', description.trim());
    const expiresIso = buildExpiresAtIso(expiresAt, expiresAtTime);
    if (expiresIso) formData.append('expires_at', expiresIso);

    setUploading(true);
    setUploadProgress(0);
    try {
      await axios.post('/api/files/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (!e.total) return;
          setUploadProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      toast.success('Файл загружен');
      closeUploadModal();
      fetchFiles();
    } catch (error) {
      toast.error(error.response?.data?.error || error.response?.data?.file?.[0] || 'Ошибка загрузки');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const openPreview = async (file) => {
    setOpenMenuId(null);
    try {
      let url;
      if (isOfficeFile(file)) {
        const { data } = await axios.post(`/api/files/${file.id}/preview-access/`);
        url = data.preview_url;
      } else {
        const response = await axios.get(`/api/files/${file.id}/download/`, {
          responseType: 'blob',
        });
        url = URL.createObjectURL(response.data);
      }
      setPreviewUrl(url);
      setPreviewFile(file);
    } catch (error) {
      toast.error('Не удалось открыть предпросмотр');
    }
  };

  const closePreview = () => {
    if (previewUrl && previewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl('');
    setPreviewFile(null);
  };

  const handleDelete = async (file) => {
    if (!window.confirm(`Удалить файл «${file.original_filename}»?`)) return;
    try {
      await axios.delete(`/api/files/${file.id}/`);
      toast.success('Файл удалён');
      fetchFiles();
    } catch (error) {
      toast.error('Ошибка удаления файла');
    }
  };

  const startEdit = (file) => {
    const expiresDate = file.expires_at ? new Date(file.expires_at) : null;
    setEditingFile(file);
    setEditDescription(file.description || '');
    setEditExpiresAt(expiresDate ? expiresDate.toISOString().slice(0, 10) : '');
    setEditExpiresAtTime(expiresDate ? expiresDate.toTimeString().slice(0, 5) : '');
    setOpenMenuId(null);
  };

  const saveEdit = async () => {
    if (!editingFile) return;
    try {
      await axios.patch(`/api/files/${editingFile.id}/`, {
        description: editDescription,
        expires_at: buildExpiresAtIso(editExpiresAt, editExpiresAtTime),
      });
      toast.success('Файл обновлён');
      setEditingFile(null);
      fetchFiles();
    } catch (error) {
      toast.error('Ошибка сохранения');
    }
  };

  const downloadFile = async (file, forceDownload = false) => {
    try {
      const response = await axios.get(`/api/files/${file.id}/download/`, {
        params: forceDownload ? { download: '1' } : undefined,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.original_filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Ошибка скачивания');
    }
  };

  const openFiltersModal = () => {
    setFilterDraft({ category, ordering });
    setShowFiltersModal(true);
  };

  const applyFilters = () => {
    setCategory(filterDraft.category);
    setOrdering(filterDraft.ordering);
    setShowFiltersModal(false);
  };

  const resetFilters = () => {
    setFilterDraft({ category: 'all', ordering: '-created_at' });
    setCategory('all');
    setOrdering('-created_at');
    setShowFiltersModal(false);
  };

  const actionHandlers = {
    onPreview: openPreview,
    onDownload: downloadFile,
    onShare: setShareFile,
    onEdit: startEdit,
    onDelete: handleDelete,
  };

  const renderGrid = () => (
    <div className="nc-file-grid">
      {files.map((file) => {
        const Icon = getCategoryIcon(file.file_category);
        return (
          <div
            key={file.id}
            className="nc-file-tile nc-file-tile--clickable"
            onClick={() => openPreview(file)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openPreview(file);
              }
            }}
            role="button"
            tabIndex={0}
            title="Открыть предпросмотр"
          >
            <div className="nc-file-tile-preview">
              <Icon className="nc-file-tile-icon" />
            </div>
            <div className="nc-file-tile-body">
              <div className="nc-file-tile-name" title={file.original_filename}>
                {file.original_filename}
              </div>
              <div className="nc-file-tile-meta">
                <span>{file.file_size_display}</span>
                <span>{formatDate(file.created_at)}</span>
              </div>
              {file.description && (
                <div className="nc-file-tile-desc">{file.description}</div>
              )}
            </div>
            <div className="nc-file-tile-footer" onClick={(e) => e.stopPropagation()}>
              <span className="nc-file-tile-badge">
                {file.file_category_display || file.file_category}
              </span>
              <div className="nc-file-tile-actions">
                {canShare && (
                  <button
                    type="button"
                    className="nc-icon-btn"
                    title="Поделиться"
                    onClick={() => setShareFile(file)}
                  >
                    <FiShare2 />
                  </button>
                )}
                <div className="nc-file-tile-menu-wrap">
                  <button
                    type="button"
                    className="nc-icon-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === file.id ? null : file.id);
                    }}
                  >
                    <FiMoreVertical />
                  </button>
                  {openMenuId === file.id && (
                    <div className="nc-file-tile-menu" onClick={(e) => e.stopPropagation()}>
                      <FileActions file={file} canShare={canShare} compact showShare={false} {...actionHandlers} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderTable = () => (
    <div className="nc-file-table-wrap">
      <table className="table nc-file-table">
        <thead>
          <tr>
            <th>Имя</th>
            <th>Тип</th>
            <th>Размер</th>
            <th>Загрузил</th>
            <th>Дата</th>
            <th>Удалить после</th>
            <th>Ссылки</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {files.map((file) => (
            <tr
              key={file.id}
              className="nc-file-table-row"
              onClick={() => openPreview(file)}
              title="Открыть предпросмотр"
            >
              <td>
                <div className="nc-file-name-cell">
                  <strong>{file.original_filename}</strong>
                  {file.description && <span>{file.description}</span>}
                </div>
              </td>
              <td>{file.file_category_display || file.file_category}</td>
              <td>{file.file_size_display}</td>
              <td>{file.uploaded_by_email || '—'}</td>
              <td>{formatDate(file.created_at)}</td>
              <td>{file.expires_at ? formatDate(file.expires_at) : '—'}</td>
              <td>{file.share_links_count || 0}</td>
              <td onClick={(e) => e.stopPropagation()}>
                <FileActions file={file} canShare={canShare} {...actionHandlers} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="nc-files-page">
      <div className="nc-files-toolbar">
        <div className="nc-files-toolbar-left">
          <h1>Файлообменник</h1>
          <span className="nc-files-count">{files.length} файлов</span>
        </div>

        <div className="nc-files-search">
          <FiSearch />
          <input
            type="search"
            placeholder="Поиск по имени или описанию..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>

        <div className="nc-files-toolbar-right">
          <button
            type="button"
            className="nc-btn nc-btn-primary"
            onClick={() => setShowUploadModal(true)}
          >
            <FiUpload /> Загрузить
          </button>
          <button type="button" className="nc-btn nc-btn-secondary" onClick={openFiltersModal}>
            <FiFilter />
            Фильтры
            {activeFiltersCount > 0 && (
              <span className="nc-filter-badge">{activeFiltersCount}</span>
            )}
          </button>
          <div className="nc-view-toggle">
            <button
              type="button"
              className={viewMode === 'grid' ? 'active' : ''}
              title="Плитка"
              onClick={() => setViewMode('grid')}
            >
              <FiGrid />
            </button>
            <button
              type="button"
              className={viewMode === 'list' ? 'active' : ''}
              title="Таблица"
              onClick={() => setViewMode('list')}
            >
              <FiList />
            </button>
          </div>
          <button type="button" className="nc-icon-btn" title="Обновить" onClick={fetchFiles}>
            <FiRefreshCw />
          </button>
        </div>
      </div>

      {isModerator && (
        <p className="nc-files-hint">Для модератора: выберите фирму в шапке страницы.</p>
      )}

      <div className="nc-files-content">
        {loading ? (
          <div className="nc-files-empty">Загрузка...</div>
        ) : files.length === 0 ? (
          <div className="nc-files-empty">
            <FiFile className="nc-files-empty-icon" />
            <p>Файлов не найдено</p>
            <button type="button" className="nc-btn nc-btn-primary" onClick={() => setShowUploadModal(true)}>
              <FiUpload /> Загрузить файл
            </button>
          </div>
        ) : viewMode === 'grid' ? renderGrid() : renderTable()}
      </div>

      {showUploadModal && (
        <div className="nc-modal-overlay" onClick={closeUploadModal}>
          <div className="nc-modal" onClick={(e) => e.stopPropagation()}>
            <div className="nc-modal-header">
              <h2>Загрузить файл</h2>
              <button type="button" className="nc-icon-btn" onClick={closeUploadModal} disabled={uploading}>
                <FiX />
              </button>
            </div>
            <div className="nc-modal-body">
              <label className={`nc-upload-dropzone ${selectedFile ? 'has-file' : ''}`}>
                <FiUpload />
                {selectedFile ? (
                  <>
                    <strong>{selectedFile.name}</strong>
                    <span>{(selectedFile.size / (1024 * 1024)).toFixed(2)} МБ</span>
                  </>
                ) : (
                  <>
                    <strong>Выберите файл</strong>
                    <span>Максимум 250 МБ</span>
                  </>
                )}
                <input type="file" hidden onChange={handleFilePick} disabled={uploading} />
              </label>

              <div className="nc-form-group">
                <label>Описание</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  placeholder="Необязательно"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={uploading}
                />
              </div>

              <div className="nc-form-group">
                <label>Автоудаление</label>
                <div className="nc-form-row">
                  <input
                    type="date"
                    className="form-input"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    disabled={uploading}
                  />
                  <input
                    type="time"
                    className="form-input"
                    value={expiresAtTime}
                    onChange={(e) => setExpiresAtTime(e.target.value)}
                    disabled={uploading}
                  />
                </div>
                <span className="nc-form-hint">Файл будет удалён автоматически после указанной даты</span>
              </div>

              {uploading && (
                <div className="nc-upload-progress">
                  <div className="nc-upload-progress-bar" style={{ width: `${uploadProgress}%` }} />
                  <span>{uploadProgress}%</span>
                </div>
              )}
            </div>
            <div className="nc-modal-footer">
              <button type="button" className="nc-btn nc-btn-secondary" onClick={closeUploadModal} disabled={uploading}>
                Отмена
              </button>
              <button
                type="button"
                className="nc-btn nc-btn-primary"
                onClick={handleUploadSubmit}
                disabled={uploading || !selectedFile}
              >
                {uploading ? `Загрузка ${uploadProgress}%` : 'Загрузить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showFiltersModal && (
        <div className="nc-modal-overlay" onClick={() => setShowFiltersModal(false)}>
          <div className="nc-modal nc-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="nc-modal-header">
              <h2>Фильтры</h2>
              <button type="button" className="nc-icon-btn" onClick={() => setShowFiltersModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="nc-modal-body">
              <div className="nc-form-group">
                <label>Тип файла</label>
                <select
                  className="form-input"
                  value={filterDraft.category}
                  onChange={(e) => setFilterDraft({ ...filterDraft, category: e.target.value })}
                >
                  {CATEGORY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
              <div className="nc-form-group">
                <label>Сортировка</label>
                <select
                  className="form-input"
                  value={filterDraft.ordering}
                  onChange={(e) => setFilterDraft({ ...filterDraft, ordering: e.target.value })}
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="nc-modal-footer">
              <button type="button" className="nc-btn nc-btn-secondary" onClick={resetFilters}>
                Сбросить
              </button>
              <button type="button" className="nc-btn nc-btn-primary" onClick={applyFilters}>
                Применить
              </button>
            </div>
          </div>
        </div>
      )}

      {editingFile && (
        <div className="nc-modal-overlay" onClick={() => setEditingFile(null)}>
          <div className="nc-modal nc-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="nc-modal-header">
              <h2>Редактировать</h2>
              <button type="button" className="nc-icon-btn" onClick={() => setEditingFile(null)}><FiX /></button>
            </div>
            <div className="nc-modal-body">
              <p className="nc-edit-filename">{editingFile.original_filename}</p>
              <div className="nc-form-group">
                <label>Описание</label>
                <textarea
                  className="form-textarea"
                  rows={3}
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </div>
              <div className="nc-form-group">
                <label>Автоудаление</label>
                <div className="nc-form-row">
                  <input
                    type="date"
                    className="form-input"
                    value={editExpiresAt}
                    onChange={(e) => setEditExpiresAt(e.target.value)}
                  />
                  <input
                    type="time"
                    className="form-input"
                    value={editExpiresAtTime}
                    onChange={(e) => setEditExpiresAtTime(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="nc-modal-footer">
              <button type="button" className="nc-btn nc-btn-primary" onClick={saveEdit}>Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {previewFile && (
        <div className="nc-modal-overlay" onClick={closePreview}>
          <div
            className={`nc-modal nc-modal-lg ${needsFillPreview(previewFile) ? 'nc-modal-preview-fill' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nc-modal-header">
              <h2>{previewFile.original_filename}</h2>
              <button type="button" className="nc-icon-btn" onClick={closePreview}><FiX /></button>
            </div>
            <div className="nc-modal-body nc-modal-body--viewer">
              <FileViewer
                file={previewFile}
                downloadUrl={previewUrl}
                fill={needsFillPreview(previewFile)}
              />
            </div>
          </div>
        </div>
      )}

      <FileShareModal
        open={!!shareFile}
        onClose={() => setShareFile(null)}
        companyFile={shareFile}
      />
    </div>
  );
};

export default FileExchange;
